<template>
  <div class="edit-profile-container" v-if="isReady">
    <div class="edit-form-title">
      <h1>Edit Profile</h1>
    </div>
    <user-profile-picture
      :url="profilePic"
      isMe
      imageType="profile"
      @change="getLoggedInUserProfile()"></user-profile-picture>
    <div class="edit-form">
      <label>Full Name<span class="required-flag">*</span></label>
      <input type="text" v-model="displayName"/>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Username
        <tooltip>
          Create a custom URL for your profile page.
        </tooltip>
      </label>
      <input type="text" v-model="userName"/>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>
        <span>Email</span>
        <span class="required-flag">*</span>
      </label>
      <input type="email" v-model="email"/>
      <span class="text-note edit-form-margin-top">
        <font-awesome-icon :icon="['fa', 'lock']" size="sm"
          :style="{ color: 'BBBBBB' }"/>
        This information will remain private.
      </span>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>
        <span>Gender</span>
        <span class="required-flag">*</span>
      </label>
      <v-select v-model="gender" class="gender"
        placeholder="Please select one" :options="['Male', 'Female', 'Other']">
      </v-select>
      <span class="text-note edit-form-margin-top">
        <font-awesome-icon :icon="['fa', 'lock']" size="sm"
          :style="{ color: 'BBBBBB' }"/>
        This information will remain private.
      </span>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>
        <span>Birthday</span>
        <span class="required-flag">*</span>
      </label>
      <date-time-picker v-model="dob" :options="{maxDate: 'today', enableTime: false}"
        outputFormat="Y-m-d" placeholder="Birthday" class="birthday">
      </date-time-picker>
      <span class="text-note edit-form-margin-top">
        <font-awesome-icon :icon="['fa', 'lock']" size="sm"
          :style="{ color: 'BBBBBB' }"/>
        <span>Your Birthday is special to us. This information will remain private.</span>
      </span>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>
        <span>Location</span>
        <span class="required-flag">*</span>
      </label>
        <v-select type="text" label="title" :options="filteredLocations" :filterable="false"
          @search="debouncedGeoCode" v-model="location" class="location">
          <template slot="no-options">
            Type to search for locations...
          </template>
          <template slot="option" slot-scope="option">
            <span><b>{{ option.title }}</b></span>
            <span>{{ option.vicinity }}</span>
          </template>
        </v-select>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Company Name</label>
      <input type="text" v-model="companyName"/>
      <span class="remaining-characters">{{50 - companyName.length}}/50</span>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>About
        <tooltip>
        YouPaired is all about building relationships.
        We want to help businesses get to know each other.
        What's your story?
        </tooltip>
      </label>
      <textarea type="text" v-model="about" rows="5" class="about"></textarea>
      <div class="remaining-characters">{{250 - about.length}}/250</div>
      <span class="text-note edit-form-margin-top">
      </span>
    </div>
    <editable-links-container class="edit-form edit-form-margin-top websites"
      title="Links" :maxLinks="4" v-model="websites" placeholder="https://facebook.com/...">
    </editable-links-container>
    <div class="edit-form" @click="submit">
      <button class="button" aria-label="Submit">Submit</button>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapMutations, mapState, mapActions } from 'vuex';
import UserProfilePicture from '@/components/user-profile/UserProfilePicture.vue';
import geoCoder from '@/components/utils/geoCoder';
import DateTimePicker from '@/components/DateTimePicker.vue';
import EditableLinksContainer from '@/components/utils/EditableLinksContainer.vue';

const mapComputedUserProperty = (key, defaultValue) => ({
  get() {
    const ret = this.$store.state.user[key];
    return ret || defaultValue;
  },
  set(val) {
    this.setUserProperty({ [key]: val });
  },
});

export default {
  name: 'edit-profile',
  components: {
    UserProfilePicture,
    DateTimePicker,
    EditableLinksContainer,
  },
  data() {
    return {
      isUploadingProfilePic: false,
      filteredLocations: [],
      isReady: false,
    };
  },
  async created() {
    this.debouncedGeoCode = debounce(this.geoCode, 250);
    await this.getLoggedInUserProfile();
    this.isReady = true;
  },
  computed: {
    ...mapState('auth', ['backendAxios']),
    ...mapState('utils', ['months', 'dates', 'years']),
    userName: mapComputedUserProperty('userName'),
    displayName: mapComputedUserProperty('displayName'),
    email: mapComputedUserProperty('email'),
    profilePic: mapComputedUserProperty('profilePic'),
    gender: mapComputedUserProperty('gender'),
    about: mapComputedUserProperty('about', ''),
    location: mapComputedUserProperty('location'),
    companyName: mapComputedUserProperty('companyName', ''),
    websites: mapComputedUserProperty('websites', []),
    dob: mapComputedUserProperty('dob'),
  },
  methods: {
    ...mapMutations('user', ['setUserProperty']),
    ...mapActions('user', ['getLoggedInUserProfile']),
    async submit() {
      try {
        const {
          displayName, userName, email, gender, dob,
          location, companyName, about, websites,
        } = this;

        const userData = {
          displayName,
          userName,
          email,
          gender,
          dob,
          location,
          companyName,
          about,
          websites,
        };

        await this.backendAxios.post('/account/profile/edit', {
          userData,
        });
        await this.getLoggedInUserProfile();

        this.$toasted.show('Saved Profile', {
          type: 'info',
          duration: 5000,
        });
      } catch (err) {
        this.$toasted.show(err.response.data, {
          type: 'info',
          duration: 5000,
        });
      }
    },

    async geoCode(searchString, loading) {
      loading(true);
      // const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${searchString}.json?access_token=${process.env.VUE_APP_MAPBOX_TOKEN}&autocomplete=true&types=postcode%2Ccountry%2Cregion%2Cdistrict%2Clocality%2Cplace%2Cneighborhood%2Caddress%2Cpoi`;
      // this.filteredLocations = [];
      // const places = await axios.get(url);
      this.filteredLocations = await geoCoder(searchString);
      loading(false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/youpaired-colors.scss';


.edit-profile-container {
  width: 70%;
  margin: 0 auto 2rem auto;
}

@media screen and (max-width: 811px) {
  .edit-profile-container {
    width: 100%;
  }
}

.profile-pic-header {
  display: flex;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1rem;
  padding: 1rem;
  background-color: #E8e8E8;
}

// .edit-profile-form {
//   display: flex;
//   align-items: top;
//   justify-content: center;
//   padding: 0.5rem;
//   margin: 0.5rem 0;
// }

// .edit-profile-form label {
//   width: 10rem;
//   padding: 0.75rem;
//   text-align: right;
//   font-size: 1rem;
// }

// .edit-profile-form input {
//   text-indent: 0.3rem;
// }

// .edit-profile-form input {
//   width: 30rem;
//   text-align: left;
//   font-size: 0.875rem;
//   font-weight: normal;
//   color: #484848;
//   background-color: white;
//   height: 2rem;
//   border: 1px solid #DCDBDB;
//   border-radius: 0.1rem;
//   cursor: pointer;
// }

// .edit-profile-form textarea {
//   width: 30rem;
//   text-align: left;
//   height: auto;
//   font-size: 0.875rem;
//   font-weight: normal;
//   color: #484848;
//   border: 1px solid #DCDBDB;
//   border-radius: 0.1rem;
//   text-indent: 0.3rem;
// }

// .edit-profile-form .dob {
//   width: 10rem;
//   text-align: left;
//   height:1.6rem;
//   font-size: 0.875rem;
//   font-weight: normal;
//   color: #484848;
//   background-color: white;
//   height: 2rem;
//   border: 1px solid #DCDBDB;
//   border-radius: 0.1rem;
//   margin: 0.1rem;
//   cursor: pointer;
// }

// .edit-profile-form .submit {
//   cursor: pointer;
//   height: 2rem;
//   padding: 0 1rem;
//   border-radius: 0.2rem;
//   color: #fff;
//   background-color: $login-background;
//   font-size: 1rem;
//   border-color: $login-background;
//   font-weight: 800;
//   margin: 2rem 0 0 0rem;
//   white-space: nowrap;
//   letter-spacing: .025em;
//   text-decoration: none;
//   box-shadow: 0 4px 6px rgba(50,50,93,.11), 0 1px 3px rgba(0,0,0,.08);

//   &:hover {
//     background-color: $login-hover;
//     border-color: $login-hover;
//   }
// }

// .about-container {
//   align-items: normal;
//   font-size: 0.875rem;
// }

// .about-container label {
//   padding-top: 0.1rem;
// }

.required-flag {
  color: red;
}

.gender, .birthday, .location, .about, .websites {
  width: 100%;
}

// .error-input {
//   background-color: $error-input !important;
// }

// .error-text {
//   color: $error-text !important;
//   font-size: 0.8rem;
//   padding-top: 0.5rem;
//   width: 30rem;
// }

.text-note {
  color: $text-note;
  width: 100%;
}

// .location-select {
//   width: 30rem;
// }

</style>

<style>

.dob-container .flatpickr-input {
  width: 30rem;
  height: 1.5rem;
  margin-right: 0;
}

</style>
